/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaWriteTargetDialog.tsx
 * FILE_ID: JOBHUB-QA-WRITE-TARGET-001
 * PURPOSE: Ask whether a new checklist joins the selected QA or starts another.
 * Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaUseTemplatesDialog.tsx (QaUseTemplatesDialog)
 */
import { useEffect, useState } from 'react'
import { MobileSheet, useIsMdUp } from '@quotech/ui'
import type { QaWriteTarget } from '../JobQaSetupCard'

type Step = 'choose' | 'name' | 'confirm'

export function QaWriteTargetDialog({
  onCancel,
  onChoose,
  onAdd,
  newName,
  replaceConfirmName,
}: {
  onCancel: () => void
  onChoose: (target: QaWriteTarget, name?: string) => void
  onAdd?: () => void
  newName?: string
  replaceConfirmName?: string
}) {
  const md = useIsMdUp()
  const [step, setStep] = useState<Step>('choose')
  const [draftName, setDraftName] = useState(newName ?? '')
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const trimmed = draftName.trim()
  const submitName = () => {
    if (!trimmed) return
    onChoose('create', trimmed)
  }

  const actions = step === 'name' ? (
    <form
      className="mt-3 flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        submitName()
      }}
    >
      <label className="text-sm text-foreground" htmlFor="qa-write-target-name">
        Name for the new QA
      </label>
      <input
        id="qa-write-target-name"
        value={draftName}
        autoFocus
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => setDraftName(event.target.value)}
        className="min-h-[44px] rounded-md border border-border bg-background px-3 text-sm"
      />
      <button
        type="submit"
        disabled={!trimmed}
        className="min-h-[44px] rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        Create
      </button>
      <button
        type="button"
        onClick={() => setStep('choose')}
        className="min-h-[44px] rounded-md border border-border px-3 text-sm font-medium"
      >
        Back
      </button>
    </form>
  ) : step === 'confirm' ? (
    <div className="mt-3 flex flex-col gap-2">
      <p className="text-sm text-foreground">
        {`Replace everything in ${replaceConfirmName}? This removes every area, checklist, check, photo and recorded result in ${replaceConfirmName}, then adds the chosen templates. The QA tab, its name and its ITP history stay. This cannot be undone.`}
      </p>
      <button
        type="button"
        onClick={() => onChoose('replace')}
        className="min-h-[44px] rounded-md bg-destructive px-3 text-sm font-medium text-destructive-foreground"
      >
        Replace
      </button>
      <button
        type="button"
        onClick={() => setStep('choose')}
        className="min-h-[44px] rounded-md border border-border px-3 text-sm font-medium"
      >
        Back
      </button>
    </div>
  ) : (
    <div className="mt-3 flex flex-col gap-2">
      <button
        type="button"
        onClick={() => (newName == null ? onChoose('create') : setStep('name'))}
        className="min-h-[44px] rounded-md border border-border px-3 text-sm font-medium"
      >
        Add as a new QA
      </button>
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className="min-h-[44px] rounded-md border border-border px-3 text-sm font-medium"
        >
          Add to selected QA
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => (replaceConfirmName == null ? onChoose('replace') : setStep('confirm'))}
        className="min-h-[44px] rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground"
      >
        Replace selected QA
      </button>
    </div>
  )

  if (!md) {
    return (
      <MobileSheet open onClose={onCancel} title="Where should this go?">
        {actions}
      </MobileSheet>
    )
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-background/70 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-labelledby="qa-write-target-title"
        className="w-full max-w-sm rounded-xl border border-border bg-card p-4 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="qa-write-target-title" className="text-sm font-semibold text-foreground">
          Where should this go?
        </h2>
        {actions}
      </div>
    </div>
  )
}
